/**
 * O que coloca um contato dentro de um fluxo.
 *
 * Mora em shared/ porque três lugares precisam concordar: o cartão "Começa
 * quando" no editor, a lista de fluxos, e o espelhamento para as tabelas que
 * o motor consulta (`server/db/flows.ts`).
 *
 * Atenção ao par de tipos. `flows.triggerType` é o vocabulário da tela
 * (keyword, tag_added, …); `flow_builder_flows.triggerType` é o do motor
 * (message, tag, broadcast, manual). O mapa `TIPO_NO_MOTOR` traduz um no
 * outro, e é ele que decide se o fluxo dispara — mudar a tela sem mudar o
 * espelho faz o gatilho novo aparecer na interface e nunca acontecer.
 */

export type TipoDeGatilho =
  | "keyword"
  | "any_message"
  | "new_contact"
  | "opt_in"
  | "tag_added"
  | "broadcast"
  | "webhook";

export type DefinicaoDeGatilho = {
  tipo: TipoDeGatilho;
  rotulo: string;
  descricao: string;
  /** Rótulo do campo de valor. `null` = este gatilho não pede valor. */
  rotuloDoValor: string | null;
  dicaDoValor?: string;
  exemploDoValor?: string;
  /** Valor é obrigatório para o gatilho funcionar. */
  valorObrigatorio?: boolean;
  /** O valor é o ID de uma etiqueta — a tela mostra um seletor, não um texto. */
  valorEhIdDeEtiqueta?: boolean;
  /**
   * Não aparece na lista de escolhas, mas continua reconhecido para os fluxos
   * que já o usam — some da tela sem travar o Salvar de quem já o tem.
   */
  descontinuado?: boolean;
};

export const GATILHOS: DefinicaoDeGatilho[] = [
  {
    tipo: "keyword",
    rotulo: "Contato envia uma palavra-chave",
    descricao: "Dispara quando a mensagem contém a palavra que você escolher",
    rotuloDoValor: "Palavra-chave",
    dicaDoValor: "O fluxo começa quando o contato escrever esta palavra na conversa.",
    exemploDoValor: "curso",
    valorObrigatorio: true,
  },
  {
    tipo: "any_message",
    rotulo: "Contato envia qualquer mensagem",
    descricao: "Toda conversa nova passa por aqui",
    rotuloDoValor: null,
  },
  {
    tipo: "new_contact",
    rotulo: "Um novo contato é criado",
    descricao: "Cadastro por formulário, importação ou integração",
    rotuloDoValor: null,
  },
  {
    tipo: "tag_added",
    rotulo: "Uma etiqueta é adicionada",
    descricao: "Roda quando o contato recebe a etiqueta",
    // O valor é o ID da etiqueta, não o nome: é por `tagId` que o despachante
    // casa o evento. Guardar o nome fazia o fluxo disparar com QUALQUER
    // etiqueta aplicada, porque `tagId` ficava nulo e vira catch-all.
    rotuloDoValor: "Etiqueta",
    valorEhIdDeEtiqueta: true,
    dicaDoValor: "O fluxo começa assim que esta etiqueta for aplicada ao contato.",
    valorObrigatorio: true,
  },
  {
    tipo: "broadcast",
    rotulo: "Você dispara numa transmissão",
    descricao: "O fluxo só roda quando você envia para uma lista",
    rotuloDoValor: null,
  },
  {
    tipo: "webhook",
    rotulo: "Outro sistema chama por webhook",
    descricao: "Zapier, Make, formulário ou API própria",
    rotuloDoValor: null,
  },
  {
    // Nada no servidor dispara opt-in: não há evento, não há rota. Ficava na
    // lista mapeado para `message` e, sem palavra-chave, respondia a TODA
    // mensagem recebida. Segue aqui só para os fluxos que já o escolheram
    // continuarem abrindo e salvando enquanto a pessoa troca o gatilho.
    tipo: "opt_in",
    rotulo: "O contato confirma o aceite",
    descricao: "Descontinuado — nada dispara este gatilho",
    rotuloDoValor: null,
    descontinuado: true,
  },
];

/** O que a tela oferece: tudo que não foi descontinuado. */
export const GATILHOS_OFERECIDOS = GATILHOS.filter((g) => !g.descontinuado);

export const GATILHO_POR_TIPO = new Map(GATILHOS.map((g) => [g.tipo, g]));

/**
 * Tradução do vocabulário da tela para o do motor.
 *
 * Cada destino é o `triggerType` que `flow-trigger-dispatcher` consulta ao
 * receber o evento correspondente — não uma classificação solta:
 *
 *  - `message`: varredura de mensagem recebida (`matchesMessage`);
 *  - `tag`: etiqueta aplicada/removida (`matchesTag`);
 *  - `webhook`: criação de contato e eventos de CRM, distinguidos por
 *    `triggerData.source`. A chamada externa NÃO entra aqui: ela dispara o
 *    fluxo por id, direto;
 *  - `broadcast`: disparado pelo dispatcher de campanha;
 *  - `manual`: nada dispara sozinho.
 *
 * `new_contact` apontava para `message`: o fluxo nunca rodava ao criar um
 * contato E, por não ter palavra-chave, respondia a TODA mensagem recebida.
 */
export const TIPO_NO_MOTOR: Record<string, "message" | "tag" | "broadcast" | "manual" | "webhook"> = {
  keyword: "message",
  any_message: "message",
  new_contact: "webhook",
  // Descontinuado: `manual` garante que não dispare sozinho. Estava em
  // `message` e respondia a toda mensagem recebida.
  opt_in: "manual",
  tag_added: "tag",
  broadcast: "broadcast",
  // `manual`, e não `webhook`: a rota `/api/webhooks/in/:urlToken` chama
  // `executeFlowById` com o flowId configurado no próprio webhook — nunca
  // passa pelo casamento de gatilhos. Mapear para `webhook` colocava esses
  // fluxos na mesma consulta dos eventos de CRM (`crmEvents` despacha
  // `{type:"webhook", source:"crm"}`), e como `matchesWebhook` sem filtro casa
  // com tudo, um fluxo de Zapier passava a rodar a cada negócio criado,
  // movido, ganho ou perdido do contato.
  webhook: "manual",
};

export function tipoNoMotor(tipoDaTela: string | null | undefined) {
  return TIPO_NO_MOTOR[tipoDaTela ?? ""] ?? "manual";
}

/**
 * O que o motor guarda em `triggerData` para casar o disparo.
 *
 * O formato muda por gatilho, e é por isso que esta função precisa do TIPO.
 * Gravar `{keywords:[…]}` para tudo fazia a etiqueta escolhida ser ignorada
 * (`matchesTag` procura `tagId`), e o fluxo disparava com qualquer etiqueta
 * aplicada ao contato.
 */
export function dadosDoGatilhoNoMotor(
  tipo: string | null | undefined,
  valor: string | null | undefined,
): Record<string, unknown> {
  const v = (valor ?? "").trim();
  switch (tipo) {
    case "keyword":
      return v ? { keywords: [v] } : {};
    case "tag_added": {
      const tagId = Number(v);
      // Sem etiqueta escolhida seria catch-all. Melhor não casar com nada —
      // o painel já cobra a escolha antes de salvar.
      return Number.isFinite(tagId) && tagId > 0
        ? { operation: "added", tagId }
        : { operation: "added", tagId: -1 };
    }
    case "new_contact":
      return { source: "new_contact" };
    default:
      // any_message, broadcast, webhook: sem filtro adicional.
      return {};
  }
}

/** Uma frase curta para o cartão: “Contato envia “curso””. */
export function resumoDoGatilho(
  tipo: string | null | undefined,
  valor: string | null | undefined,
  nomeDaEtiqueta?: string | null,
): string {
  const def = GATILHO_POR_TIPO.get((tipo ?? "") as TipoDeGatilho);
  if (!def) return "Escolha o que inicia este fluxo";

  const v = (valor ?? "").trim();
  if (def.tipo === "keyword") return v ? `Contato envia “${v}”` : "Escolha a palavra-chave";
  if (def.tipo === "tag_added") {
    // `valor` é o ID da etiqueta; o NOME vem da tela, que tem a lista carregada.
    if (!v) return "Escolha a etiqueta";
    return nomeDaEtiqueta
      ? `O contato recebe a etiqueta “${nomeDaEtiqueta}”`
      : "O contato recebe a etiqueta escolhida";
  }
  return def.rotulo;
}

/** `null` quando o gatilho está pronto; senão, o que falta nele. */
export function pendenciaDoGatilho(
  tipo: string | null | undefined,
  valor: string | null | undefined,
  /**
   * `false` quando o valor é de etiqueta e não corresponde a nenhuma do
   * workspace — nome renomeado, apagado, ou gravado pela API. O espelhamento
   * grava `tagId: -1` nesse caso (não casa com nada), e sem este aviso o
   * fluxo ficaria ativo sem nunca disparar.
   */
  etiquetaExiste?: boolean,
): { curta: string; longa: string; bloqueia: boolean } | null {
  const def = GATILHO_POR_TIPO.get((tipo ?? "") as TipoDeGatilho);
  if (!def) {
    return {
      curta: "Gatilho não reconhecido",
      // Não bloqueia: um fluxo antigo com gatilho que saiu do catálogo
      // precisa poder ser aberto e salvo enquanto a pessoa troca o gatilho.
      bloqueia: false,
      longa: "Este gatilho não existe mais. Abra “Começa quando” e escolha outro — enquanto isso o fluxo não dispara.",
    };
  }
  if (def.descontinuado) {
    return {
      curta: "Gatilho descontinuado",
      bloqueia: false,
      longa: `“${def.rotulo}” não é disparado por nada no sistema. Abra “Começa quando” e escolha outro.`,
    };
  }
  if (def.valorObrigatorio && !(valor ?? "").trim()) {
    return {
      curta: `Falta ${def.rotuloDoValor?.toLowerCase() ?? "o valor"}`,
      bloqueia: true,
      longa: `O gatilho “${def.rotulo}” não funciona sem ${def.rotuloDoValor?.toLowerCase()}. Abra “Começa quando” e preencha.`,
    };
  }
  if (def.valorEhIdDeEtiqueta && etiquetaExiste === false) {
    return {
      curta: "Etiqueta não existe mais",
      bloqueia: true,
      longa: "A etiqueta escolhida foi renomeada ou apagada. Abra “Começa quando” e escolha outra — enquanto isso o fluxo não dispara.",
    };
  }
  return null;
}
